import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DialogService } from './dialog.service';
import { LockStateService } from './lock-state.service';
import { SAVE_FAILED_TEXT } from './session-policy';

export interface PendingRequest {
  id: string;
  key: string;          // Dedup key: "POST:/api/vitals:visitId=42"
  url: string;
  method: string;
  body: any;
  label: string;        // "Save Vitals — Ramesh Kumar"
  timestamp: number;
  savedInPage?: string;  // TC-4527: which page load saved it, and that page's running clock then. See ageMs.
  savedAtPerf?: number;
  retryCount: number;
  refusedBecause?: string;  // the server said no: retrying won't help until that is fixed, so the reason is shown
  staffId?: number;     // GO-LIVE FIX: who queued it — shared-PC guard. localStorage outlives
                        // logout, so without this the NEXT user's "Send All" replayed the
                        // PREVIOUS user's items under the new user's token (wrong attribution).
  hospitalId?: number;  // TC-5988: which hospital it was saved in — a support session carries the OWNER's staffId for
                        // every hospital, so staffId alone offered Hospital A's unsaved item inside Hospital B's session.
}

/** An offline item that expired (72h) and was dropped, kept until its owner presses "I've noted these". */
export interface ExpiredOfflineItem {
  staffId: number;
  label: string;
  timestamp: number;
}

/**
 * NETWORK GUARD SERVICE
 * Protects hospital staff from silent data loss when network drops.
 *
 * KEY DESIGN — Duplicate clicks while offline:
 *   Click 1: "Save Vitals" → queued as key "POST:/api/vitals:v42"
 *   Click 2: "Save Vitals" → SAME key → REPLACES old data (latest wins)
 *   Click 3: "Save Vitals" → SAME key → REPLACES again
 *   Network back → only 1 item in queue (the latest data) ✅
 *
 * Usage in components:
 *   // Before HTTP call:
 *   if (!this.networkGuard.isOnline) {
 *     this.networkGuard.queueRequest(
 *       '/api/vitals', 'POST', payload,
 *       'Save Vitals — Ramesh Kumar',
 *       `vitals:visit:${visitId}`       // ← dedup key
 *     );
 *     return; // Don't make HTTP call
 *   }
 */

/** 2026-11-01: older builds queued doctor notes, test orders, admissions / discharge approvals and vitals edits under
 *  addresses the API never had (404 on every replay, so the work never reached the server). Items already waiting in
 *  a browser are rewritten to the real address when they are sent. */
const LEGACY_QUEUE_URLS: ReadonlyArray<[RegExp, string]> = [
  [/^\/api\/doctornotes$/, '/api/doctor-notes'],
  [/^\/api\/testrequests\/batch$/, '/api/test-requests/batch'],
  [/^\/api\/admissions(?=\/|$)/, '/api/admission'],
  [/^\/api\/nurse\/vitals\//, '/api/vitals/'],
];

/** The address a queued item is sent to (see LEGACY_QUEUE_URLS). */
export function currentQueueUrl(url: string): string {
  for (const [from, to] of LEGACY_QUEUE_URLS) if (from.test(url)) return url.replace(from, to);
  return url;
}

/** Method + body a queued item is sent with. BLK-029 (2026-11-02): older builds queued the doctor's "Mark Seen" as
 *  PATCH { status } — the API takes PUT { doctorStatus }, so it failed on every replay. */
export function currentQueueRequest(item: { url: string; method: string; body: any }): { url: string; method: string; body: any } {
  const url = currentQueueUrl(item.url);
  if (/^\/api\/visits\/\d+\/doctor-status$/.test(url) && item.method.toUpperCase() === 'PATCH') {
    return { url, method: 'PUT', body: { doctorStatus: item.body?.doctorStatus ?? item.body?.status } };
  }
  // TC-5972 (live re-test 2026-11-02): money is online-only. A queued registration used to replay "OP fee paid", stamping a
  // cash payment at replay time under whoever pressed Send All (the drawer never matches). It now creates the patient with
  // the fee unpaid. Safe for a registration whose reply was lost: its key returns the original, with its real payment.
  if (url === '/api/patients/register') {
    return { url, method: item.method, body: { ...item.body, markOPAsPaid: false, opPaymentMethod: null, opPaymentReference: null, forceDuplicatePhone: true } };
  }
  return { url, method: item.method, body: item.body };
}

/** Owner decision 2026-11-02: a note saved offline waited for someone to press "Send All", so a 15-minute cut could keep a
 *  doctor's order from the nurse until the next day. Items now send themselves when the internet returns. A person still
 *  checks: set-up changes (replaying one could undo what a colleague changed since), anything older than a shift (it may no
 *  longer apply: the patient may have gone home), and anything the server refused. */
const ASK_BEFORE_SENDING: ReadonlyArray<RegExp> = [/^\/api\/hospital-settings/, /^\/api\/auth\//, /^\/api\/hospital-tests/, /^\/api\/medicine-stock/];
const AUTO_SEND_MAX_AGE_MS = 8 * 60 * 60 * 1000;
const AUTO_SEND_SETTLE_MS = 3000;   // the moment the network returns it is often not usable yet
const AUTO_RETRY_MS = [30_000, 60_000, 120_000, 300_000];

/** Why a person has to look at this item first, or null when it can send by itself. */
export function whyAskFirst(item: { url: string; timestamp: number; refusedBecause?: string }, now = Date.now()): string | null {
  if (item.refusedBecause) return `Not saved: ${item.refusedBecause}`;
  if (ASK_BEFORE_SENDING.some(re => re.test(item.url))) return 'A set-up change: check it before sending, a colleague may have changed this since.';
  if (now - item.timestamp >= AUTO_SEND_MAX_AGE_MS) return 'Saved more than 8 hours ago: check it still applies before sending.';
  return null;
}

@Injectable({ providedIn: 'root' })
export class NetworkGuardService implements OnDestroy {

  private onlineStatus$ = new BehaviorSubject<boolean>(navigator.onLine);
  private bannerElement: HTMLElement | null = null;
  private retryPanelElement: HTMLElement | null = null;
  private expiredAlertElement: HTMLElement | null = null;
  private staleTimer: any = null;
  private autoTimer: any = null;
  private startupTimer: any = null;
  private autoRetries = 0;
  private panelShowsAll = false;   // opened from the header: everything still waiting, not just what needs a decision
  private sendStopped = false;     // the last send met an expired session or a blocked hospital: trying again won't help
  private pendingSubject = new BehaviorSubject<number>(0);
  /** How many of the signed-in person's items have not reached the server (the header's "N not sent"). */
  readonly pending$ = this.pendingSubject.asObservable();

  private lastSaveFailedToast: HTMLElement | null = null;
  private readonly QUEUE_KEY = 'meditrack_offline_queue';
  private readonly MAX_QUEUE_SIZE = 50;
  // GO-LIVE FIX: was 24h — a save queued Friday evening was SILENTLY deleted before
  // Monday. 72h survives a weekend; expiry is now also announced (see cleanStaleItems).
  private readonly MAX_AGE_MS = 72 * 60 * 60 * 1000; // 72 hours
  private readonly pageLoadId = NetworkGuardService.newItemId();
  private readonly SEND_LOCK_KEY = 'meditrack_offline_sending';
  // B052/TC-0076: expired items are remembered (per staffId) until acknowledged, so the must-acknowledge notice
  // survives a sign-out (which removes it from screen) and a lock (which hides it) and returns for its owner.
  private readonly EXPIRED_KEY = 'meditrack_offline_expired';

  private onlineFn  = () => this.handleOnline();
  private offlineFn = () => this.handleOffline();
  private storageFn = (e: StorageEvent) => { if (e.key === this.QUEUE_KEY) this.refreshPending(); };   // another tab sent them

  constructor(private http: HttpClient, private dialog: DialogService, private lockState: LockStateService) {
    window.addEventListener('online', this.onlineFn);
    window.addEventListener('offline', this.offlineFn);
    window.addEventListener('storage', this.storageFn);

    if (!navigator.onLine) {
      this.handleOffline();
    }

    this.dropUnsafeQueuedItems();
    this.cleanStaleItems();

    // GO-LIVE FIX: re-surface pending items at app load. Previously items that failed a
    // retry (or were queued just before a reload) sat INVISIBLE until the next
    // offline→online transition, then aged out silently. Delay lets bootstrap/login settle;
    // skipped when no user is logged in (login page — token would be empty anyway) or while locked.
    this.startupTimer = setTimeout(() => this.resurfaceForCurrentUser(), 8000);

    // PATIENT-SAFETY FIX: re-check expiry while the app stays open for days. cleanStaleItems
    // only ALERTS when an item actually crosses the 72h cutoff, so this is silent unless
    // something expired — then it raises the persistent alert promptly (not at next boot).
    this.staleTimer = setInterval(() => this.cleanStaleItems(), 30 * 60 * 1000);
  }

  // ── Per-user scoping (shared hospital PCs) ─────────────────────
  /** StaffId of the currently logged-in user, or null on the login page. */
  private currentStaffId(): number | null {
    try {
      const stored = sessionStorage.getItem('meditrack_user');
      if (!stored) return null;
      const id = JSON.parse(stored)?.staffId;
      const n = typeof id === 'number' ? id : Number(id);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch { return null; }
  }

  /** The signed-in hospital, and whether this is the owner's support session into it. */
  private currentSession(): { hospitalId: number | null; support: boolean } {
    try {
      const u = JSON.parse(sessionStorage.getItem('meditrack_user') || 'null');
      const h = Number(u?.hospitalId);
      return { hospitalId: Number.isFinite(h) && h > 0 ? h : null, support: !!u?.support };
    } catch { return { hospitalId: null, support: false }; }
  }

  /** Items the CURRENT user may see/send: their own + legacy unstamped entries, in the hospital they were saved in
   *  (TC-5988; an unstamped item is never offered inside a support session, where its hospital can't be known).
   *  Other users' items are left untouched for THEIR next login. */
  private ownItems(queue: PendingRequest[]): PendingRequest[] {
    const me = this.currentStaffId();
    const session = this.currentSession();
    return queue.filter(q => (q.staffId == null || (me != null && q.staffId === me))
      && (q.hospitalId != null ? q.hospitalId === session.hospitalId : !session.support));
  }

  /** Sign-out: one person's unsaved-item labels (patient names/phones) never stay on screen for the next person.
   *  The queue and the unacknowledged expired list stay in localStorage for that person's next sign-in. */
  removeUserPanels(): void {
    this.cancelAutoSend();
    this.removeRetryPanel();
    this.removeExpiredAlert();
    this.pendingSubject.next(0);
  }

  private refreshPending(): void {
    this.pendingSubject.next(this.currentStaffId() == null ? 0 : this.pendingCount);
  }

  /** Re-offer the CURRENT user's pending items and unacknowledged expired notice — after sign-in, after unlock and at
   *  app load. Nothing is shown with nobody signed in or while the tab is locked. */
  resurfaceForCurrentUser(): void {
    if (this.currentStaffId() == null || this.lockState.isLocked) return;
    this.cleanStaleItems();
    this.showExpiredAlertForCurrentUser();
    this.refreshPending();
    if (!navigator.onLine) return;
    if (this.pendingCount > 0) { this.autoRetries = 0; void this.autoSend(); }
  }

  /** The header's "N not sent": everything still waiting, with the choice to send now or discard. */
  showPendingPanel(): void {
    if (this.currentStaffId() == null || this.lockState.isLocked) return;
    const pending = this.ownItems(this.getQueue());
    if (pending.length === 0) { this.removeRetryPanel(); return; }
    this.panelShowsAll = true;
    this.showRetryPanel(pending);
  }

  /** One attempt now, for the sign-out warning: nothing asks to be sent, only what can go by itself goes. */
  async sendNow(): Promise<void> {
    if (!navigator.onLine) return;
    await this.autoSend();
  }

  private cancelAutoSend(): void {
    if (this.autoTimer) { clearTimeout(this.autoTimer); this.autoTimer = null; }
  }

  private scheduleAutoSend(delay: number): void {
    this.cancelAutoSend();
    this.autoTimer = setTimeout(() => { void this.autoSend(); }, delay);
  }

  /** Sends what can go by itself, shows what needs a person, and keeps trying while the server can't be reached. */
  private async autoSend(): Promise<void> {
    this.cancelAutoSend();
    if (this.currentStaffId() == null || this.lockState.isLocked || !navigator.onLine) return;

    const now = Date.now();
    const canGo = (q: PendingRequest) => whyAskFirst(q, now) === null;
    this.sendStopped = false;
    if (this.ownItems(this.getQueue()).some(canGo)) await this.retryAll({ only: canGo, quiet: true });
    this.refreshPending();

    const left = this.ownItems(this.getQueue());
    const show = this.panelShowsAll ? left : left.filter(q => whyAskFirst(q, now) !== null);
    if (show.length > 0 && !this.sendStopped) this.showRetryPanel(show);
    else this.removeRetryPanel();

    if (!this.sendStopped && left.some(canGo)) {
      this.scheduleAutoSend(AUTO_RETRY_MS[Math.min(this.autoRetries++, AUTO_RETRY_MS.length - 1)]);
    } else {
      this.autoRetries = 0;
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.onlineFn);
    window.removeEventListener('offline', this.offlineFn);
    window.removeEventListener('storage', this.storageFn);
    this.cancelAutoSend();
    if (this.startupTimer) clearTimeout(this.startupTimer);
    if (this.staleTimer) clearInterval(this.staleTimer);
    this.removeBanner();
    this.removeRetryPanel();
    this.removeExpiredAlert();
  }

  get isOnline(): boolean {
    return navigator.onLine;
  }

  get online$() {
    return this.onlineStatus$.asObservable();
  }

  get pendingCount(): number {
    // Current user's items only — another user's queued saves on a shared PC
    // are not this user's pending work.
    return this.ownItems(this.getQueue()).length;
  }

  // ══════════════════════════════════════════════════════════
  //  QUEUE MANAGEMENT — with DEDUPLICATION
  // ══════════════════════════════════════════════════════════

  /**
   * Queue a failed save for later retry.
   *
   * @param url      API endpoint (e.g. '/api/vitals')
   * @param method   HTTP method ('POST', 'PUT', 'PATCH')
   * @param body     Request body
   * @param label    Human-readable label for display
   * @param dedupKey UNIQUE key for deduplication.
   *                 Same key = REPLACE (latest data wins, no duplicates).
   *                 Examples:
   *                   "vitals:visit:42"         — vitals for visit 42
   *                   "doctornote:visit:42"      — doctor note for visit 42
   *                   "prescription:visit:42"    — prescription for visit 42
   *                   "patient:register:phone:9876543210" — patient registration
   * @returns false when it could NOT be saved. Say so to the user and keep what they typed: live re-test 2026-11-02 (TC-4137)
   *          found a dispatch form closing as if saved when nothing was, with only a 4-second toast to say so.
   */
  queueRequest(url: string, method: string, body: any, label: string, dedupKey?: string): boolean {
    let queue = this.getQueue();
    const key = dedupKey || `${method}:${url}`;
    const me = this.currentStaffId();

    // ── DEDUP: If same key exists FOR THIS USER, REPLACE it (latest data wins) ──
    // Scoped to the current user so two staff on a shared PC can't overwrite each
    // other's queued saves for the same visit (keys like "vitals:visit:42" collide).
    const hospitalId = this.currentSession().hospitalId;
    const existingIdx = queue.findIndex(q => q.key === key && (q.staffId == null || q.staffId === me)
      && (q.hospitalId == null || q.hospitalId === hospitalId));
    if (existingIdx >= 0) {
      // Update the existing entry with new data. A new id: if the old version reached the server but its reply was lost,
      // the server must not answer "already saved" for this changed one.
      queue[existingIdx].id = NetworkGuardService.newItemId();
      queue[existingIdx].body = body;
      queue[existingIdx].timestamp = Date.now();
      queue[existingIdx].savedInPage = this.pageLoadId;
      queue[existingIdx].savedAtPerf = performance.now();
      queue[existingIdx].label = label;
      if (me != null) queue[existingIdx].staffId = me;
      if (hospitalId != null) queue[existingIdx].hospitalId = hospitalId;
      if (!this.saveQueue(queue)) {
        this.notSaved(`"${label}" could not be updated: this device's storage is full. Reconnect to send it, or free up space.`);
        return false;
      }
      this.showToast(`Updated offline save: ${label}`, 'warning');
      this.keepTryingIfConnected();
      return true;
    }

    // ── NEW entry ──
    if (queue.length >= this.MAX_QUEUE_SIZE) {
      // AUDIT FIX (2026-06-16): the old global queue.shift() removed the GLOBALLY-oldest item, which on a
      // shared PC could silently discard ANOTHER staff member's queued clinical write. Evict only the CURRENT
      // user's own oldest item; if the queue is full of other users' items, refuse rather than drop their data.
      // TC-4218 (2026-11-03): an unsent ambulance dispatch is never the item pushed out.
      const mineIdx = queue.findIndex(q => (q.staffId == null || q.staffId === me) && !q.url.includes('/ambulance/dispatch'));
      if (mineIdx >= 0) {
        const dropped = queue.splice(mineIdx, 1)[0];
        // The same must-acknowledge notice as an expired item: a 4-second toast is not enough for lost clinical work.
        this.rememberExpired([{ staffId: dropped.staffId ?? me ?? 0, label: `${dropped.label} — dropped, the offline queue was full`, timestamp: dropped.timestamp }]);
        this.showExpiredAlertForCurrentUser();
      } else {
        this.notSaved(`"${label}" could not be saved: this computer's offline store is full of unsent items that can't be dropped. Reconnect, or do this another way.`);
        return false;
      }
    }

    queue.push({
      id: NetworkGuardService.newItemId(),
      key,
      url,
      method,
      body,
      label,
      timestamp: Date.now(),
      savedInPage: this.pageLoadId,
      savedAtPerf: performance.now(),
      retryCount: 0,
      staffId: me ?? undefined,
      hospitalId: hospitalId ?? undefined
    });

    if (!this.saveQueue(queue)) {
      // TC-6130: localStorage is full (a large queued item or many items) — do NOT claim the save worked.
      this.notSaved(`"${label}" could not be saved offline: this device's storage is full. Reconnect to save it now, or free up space and try again.`);
      return false;
    }
    // TC-6133: the interceptor has just said "Save failed, try again" for this request. It's kept, so retrying would send it twice.
    this.lastSaveFailedToast?.remove();
    this.showToast(`Saved offline: ${label}. Other staff can't see this until it is sent. If urgent, tell them directly.`, 'warning', 8000);
    this.keepTryingIfConnected();
    return true;
  }

  /** Nothing was kept, so this has to be read: a pop-up, not a toast. */
  private notSaved(message: string): void {
    this.dialog.alert(`NOT saved and NOT sent\n\n${message}`, { tone: 'danger' });
  }

  private static newItemId(): string {
    return `req-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  }

  /** Saved after the connection dropped mid-request, with the browser still believing it is online: no "online" event will
   *  come to send it, so a timer has to. */
  private keepTryingIfConnected(): void {
    if (navigator.onLine && !this.autoTimer) this.scheduleAutoSend(AUTO_RETRY_MS[0]);
  }

  getQueue(): PendingRequest[] {
    try {
      const raw = localStorage.getItem(this.QUEUE_KEY);
      const list = raw ? JSON.parse(raw) : [];
      // DISC-014: damaged or edited storage must not break this service (AuthInterceptor injects it) or hide the panels.
      return Array.isArray(list) ? list.filter(q => q != null && typeof q === 'object') : [];
    } catch {
      return [];
    }
  }

  removeFromQueue(id: string): void {
    const queue = this.getQueue().filter(q => q.id !== id);
    this.saveQueue(queue);
  }

  /** "Discard All" — removes only the CURRENT user's items (plus legacy unstamped);
   *  another user's queued saves on a shared PC are preserved for their next login. */
  clearQueue(): void {
    const me = this.currentStaffId();
    // With nobody signed in, "Discard All" must never delete other people's queued clinical saves
    // (every item would otherwise count as "not someone else's" and the whole key would be removed).
    if (me == null) { this.removeRetryPanel(); return; }
    const queue = this.getQueue();
    const mine = this.ownItems(queue);
    const others = queue.filter(q => !mine.includes(q));
    if (others.length > 0) this.saveQueue(others);
    else localStorage.removeItem(this.QUEUE_KEY);
    this.refreshPending();
    this.removeRetryPanel();
  }

  /** Persists the queue. Returns false when localStorage is full/blocked (TC-6130) so a NEW save can
   *  tell the user it did not work, instead of silently throwing and falsely claiming "Saved offline". */
  private saveQueue(queue: PendingRequest[]): boolean {
    try {
      localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
      this.refreshPending();
      return true;
    } catch {
      return false;
    }
  }

  /** TC-4527 (2026-11-03): a PC clock wrong by days, corrected on reconnect as Windows does, made a minutes-old reading look days
   *  old, and it was thrown away. While the page that saved it is still open, the page's own running clock isn't touched by
   *  that, so it decides. After a reload only the wall clock is left. */
  private ageMs(q: PendingRequest, now = Date.now()): number {
    return q.savedInPage === this.pageLoadId && q.savedAtPerf != null ? performance.now() - q.savedAtPerf : now - q.timestamp;
  }

  private cleanStaleItems(): void {
    // PATIENT-SAFETY FIX: never drop before the OWNER can see it. On the login page (no
    // user) leave the queue intact so expired items are announced AFTER the owner logs in,
    // rather than being silently deleted while the app sits unauthenticated.
    const me = this.currentStaffId();
    if (me == null) return;
    // Never drop or announce while locked — the alert would be hidden and could be removed at sign-out;
    // unlock runs this again.
    if (this.lockState.isLocked) return;

    const queue = this.getQueue();
    const now = Date.now();
    const stale = queue.filter(q => this.ageMs(q, now) >= this.MAX_AGE_MS);
    if (stale.length === 0) return;

    const fresh = queue.filter(q => this.ageMs(q, now) < this.MAX_AGE_MS);
    this.saveQueue(fresh);

    // Dropping queued clinical/financial writes must NOT be silent. A 4s toast was easy to
    // miss; show a PERSISTENT, must-acknowledge alert listing exactly what expired so staff
    // can re-enter it. Scope the alert to THIS user (others' items expire silently for them).
    const mine = stale.filter(q => q.staffId == null || q.staffId === me);
    if (mine.length > 0) {
      this.rememberExpired(mine.map(q => ({ staffId: q.staffId ?? me, label: q.label, timestamp: q.timestamp })));
      this.showExpiredAlertForCurrentUser();
    }
  }

  /** Expired items not yet acknowledged, for every user of this browser. */
  getExpired(): ExpiredOfflineItem[] {
    try {
      const raw = localStorage.getItem(this.EXPIRED_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(e => e != null && typeof e === 'object') : [];
    } catch {
      return [];
    }
  }

  /** BLK-029 (2026-11-02): creating a hospital carried the new admin's password and was queued offline, so the password
   *  sat in this browser's storage in plain text until it was replayed. It is no longer queued; drop any such item an
   *  older build left behind and tell its owner (expired-items alert) to create the hospital again. */
  private dropUnsafeQueuedItems(): void {
    // TC-1072: a bed transfer changes ward charges from the moment it happens — replaying an old one later bills the
    // wrong time, so it is online-only now too.
    const reasons: ReadonlyArray<[RegExp, string]> = [
      [/^\/api\/auth\/hospitals$/, 'creating a hospital needs a live connection; please create it again'],
      [/^\/api\/ip-billing\/transfer$/, 'a bed transfer changes ward charges, so it needs a live connection; please transfer again'],
    ];
    const queue = this.getQueue();
    const unsafe = queue.filter(q => reasons.some(([re]) => re.test(q.url)));
    if (unsafe.length === 0) return;
    this.saveQueue(queue.filter(q => !unsafe.includes(q)));
    this.rememberExpired(unsafe.map(q => ({
      staffId: q.staffId ?? 0, timestamp: q.timestamp,
      label: `${q.label} — not sent (${reasons.find(([re]) => re.test(q.url))![1]})`,
    })));
  }

  private rememberExpired(add: ExpiredOfflineItem[]): void {
    try {
      localStorage.setItem(this.EXPIRED_KEY, JSON.stringify([...this.getExpired(), ...add].slice(-100)));
    } catch {}
  }

  /** "I've noted these" — drop only the CURRENT user's entries; other users' notices wait for their sign-in. */
  private forgetExpiredForCurrentUser(): void {
    const me = this.currentStaffId();
    const others = this.getExpired().filter(e => e.staffId !== me);
    try {
      if (others.length > 0) localStorage.setItem(this.EXPIRED_KEY, JSON.stringify(others));
      else localStorage.removeItem(this.EXPIRED_KEY);
    } catch {}
  }

  private showExpiredAlertForCurrentUser(): void {
    const me = this.currentStaffId();
    if (me == null) return;
    const mine = this.getExpired().filter(e => e.staffId === me);
    if (mine.length > 0) this.showExpiredAlert(mine);
    else this.removeExpiredAlert();
  }

  // ══════════════════════════════════════════════════════════
  //  SENDING — by itself when it can, or when staff press Send All
  // ══════════════════════════════════════════════════════════

  /**
   * Retry the CURRENT USER's queued items: all of them from the retry panel, or those that can go by themselves
   * (quiet: nobody asked, so a network failure says nothing and the timer tries again). Removes successful items.
   */
  async retryAll(opts: { only?: (q: PendingRequest) => boolean; quiet?: boolean } = {}): Promise<void> {
    // TC-5977 (live re-test 2026-11-02): the storage lock below reads then writes, so two tabs pressing Send All together
    // both got through and every item was saved twice (2 vitals rows each, seen live). The browser's own lock is atomic.
    const locks = (navigator as any).locks;
    if (!locks?.request) return this.sendOwnItems(opts);
    let sent = false;
    await locks.request('meditrack_offline_send', { ifAvailable: true }, async (lock: unknown) => {
      if (!lock) return;
      sent = true;
      await this.sendOwnItems(opts);
    });
    if (!sent && !opts.quiet) this.showToast('Offline items are already being sent in another tab.', 'warning');
  }

  private async sendOwnItems(opts: { only?: (q: PendingRequest) => boolean; quiet?: boolean } = {}): Promise<void> {
    // GO-LIVE FIX: cross-tab send lock (best effort). Two open tabs both receive the
    // 'online' event and both show the panel — without the lock, both replay the same
    // queue and double-POST endpoints that have no server-side idempotency key.
    const now = Date.now();
    try {
      const lock = Number(localStorage.getItem(this.SEND_LOCK_KEY) || 0);
      if (lock && now - lock < 60_000) {
        if (!opts.quiet) this.showToast('Offline items are already being sent in another tab.', 'warning');
        return;
      }
      localStorage.setItem(this.SEND_LOCK_KEY, String(now));
    } catch {}

    try {
      // GO-LIVE FIX: send only the current user's items — never replay another
      // user's queued saves under this user's token (shared-PC scenario).
      const queue = this.ownItems(this.getQueue()).filter(opts.only ?? (() => true));
      if (queue.length === 0) { if (!opts.quiet) this.removeRetryPanel(); return; }

      let success = 0;
      let failed = 0;
      let refused = 0;
      let authStopped = false;
      let accessStopped = false;   // TC-6195: a 403 (hospital read-only/suspended) is NOT an expired session
      let accessMessage = '';

      for (const item of queue) {
        try {
          // Token is stored inside meditrack_user JSON object, not as a separate key
          // sessionStorage matches AuthService — see auth.service.ts AUTH_KEY note.
          const stored = sessionStorage.getItem('meditrack_user');
          let token = '';
          if (stored) {
            try { token = JSON.parse(stored)?.token || ''; } catch {}
          }
          const headers: any = { 'Content-Type': 'application/json' };
          if (token) headers['Authorization'] = `Bearer ${token}`;
          // AUDIT FIX (2026-06-16): bypass the client double-click dedupe guard on replay.
          // Replay de-duplication is the SERVER Idempotency-Key's job; letting the dedupe
          // interceptor swallow a replayed item to EMPTY would surface here as `undefined`
          // and be misread as a swallowed-401 (the check just below), wrongly halting the queue.
          headers['X-Allow-Duplicate'] = 'true';
          headers['X-Offline-Saved-At'] = new Date(item.timestamp).toISOString();   // a reading charts at the time it was taken
          headers['X-Offline-Item-Id'] = item.id;   // if the reply is lost and this is sent again, it is saved once
          if (opts.quiet) headers['X-No-Toast'] = 'true';   // nobody pressed anything: "Save failed, try again" would make them write it twice

          // Prepend API base URL for relative paths (queued as /api/...)
          const baseUrl = environment.apiUrl.replace(/\/api$/, '');
          const send = currentQueueRequest(item);
          const fullUrl = send.url.startsWith('http') ? send.url : `${baseUrl}${send.url}`;
          const res = await this.http.request(send.method, fullUrl, {
            body: send.body,
            headers
          }).toPromise();

          // AUDIT FIX (2026-06-16): the auth interceptor SWALLOWS a 401 and completes the stream with NO
          // emission (EMPTY → toPromise resolves `undefined`) instead of throwing — the old code then treated
          // that as success and DELETED the queued clinical write (silent data loss on an expired token). A
          // real successful write always emits a defined body (a 204 emits `null`, not `undefined`), so an
          // `undefined` result here means the request was dropped: KEEP the item and stop, like an explicit 401.
          if (res === undefined) {
            const stored = this.getQueue();
            const live = stored.find(q => q.id === item.id);
            if (live) { live.retryCount++; this.saveQueue(stored); }
            failed++;
            authStopped = true;
            break;
          }

          this.removeFromQueue(item.id);
          success++;
        } catch (err: any) {
          // Persist the retryCount bump on the STORED copy (the in-memory one is a snapshot).
          const stored = this.getQueue();
          const live = stored.find(q => q.id === item.id);
          const refusedBecause = NetworkGuardService.refusalReason(err);
          if (refusedBecause) refused++;
          if (live) { live.retryCount++; live.refusedBecause = refusedBecause; this.saveQueue(stored); }
          failed++;

          // GO-LIVE FIX: token expired — sending the rest would just 401 too. Stop, KEEP everything, log in again.
          if (err?.status === 401) {
            authStopped = true;
            break;
          }
          // TC-6195: a 403 means the hospital is read-only or suspended, NOT an expired session. Stop and KEEP
          // everything (writes are blocked by policy, not auth), and show the hospital's actual reason.
          // TC-4532 (2026-11-03): any 403 stopped everything, so one action the nurse may no longer do held back good clinical notes until they expired 3 days
          // later. Only a hospital-wide block stops the run. A refusal of this one item is shown beside it, and the rest go on.
          if (err?.status === 403 && !/^HOSPITAL_/.test(err?.error?.code || '') && !err?.error?.mustChangePassword) {
            const refusedStored = this.getQueue();
            const refusedLive = refusedStored.find(q => q.id === item.id);
            if (refusedLive) { refusedLive.refusedBecause = String(err?.error?.message || 'You are not allowed to do this.').slice(0, 200); this.saveQueue(refusedStored); }
            refused++;
            continue;
          }
          if (err?.status === 403) {
            accessStopped = true;
            accessMessage = err?.error?.message || err?.error?.detail
              || 'Your hospital’s access is currently read-only or suspended, so these items were not sent. They are kept and will be offered when access is restored.';
            break;
          }
        }
      }

      this.sendStopped = authStopped || accessStopped;
      this.refreshPending();
      if (authStopped) {
        this.showToast('Session expired — please log in again. Your unsaved items are kept and will be offered after login.', 'error');
        this.removeRetryPanel();
      } else if (accessStopped) {
        // TC-6195: read-only / suspended hospital — show the real reason, keep the items (no forced re-login).
        this.showToast(accessMessage, 'error');
        this.removeRetryPanel();
      } else if (success > 0 && failed === 0) {
        this.showToast(`All ${success} item(s) saved successfully!`, 'success');
        if (!opts.quiet) this.removeRetryPanel();
      } else if (opts.quiet) {
        if (refused > 0) this.showToast(`${success > 0 ? `${success} saved. ` : ''}${refused} refused by the server: the reason is shown next to each item.`, 'error');
        else if (success > 0) this.showToast(`${success} saved. ${failed} not sent yet: trying again by itself.`, 'warning');
      } else {
        // Live re-test 2026-11-02: a refusal read "server may still be unreachable", so staff kept retrying something that
        // could never go through, with no reason shown, until it expired 3 days later.
        if (refused > 0) this.showToast(`${success > 0 ? `${success} saved. ` : ''}${refused} refused by the server: the reason is shown next to each item.`, 'error');
        else if (success > 0) this.showToast(`${success} saved, ${failed} still failed.`, 'warning');
        else this.showToast(`Retry failed — server may still be unreachable.`, 'error');
        // GO-LIVE FIX: re-surface the panel with what's left. Previously failed items
        // sat INVISIBLE until the next offline→online cycle, then aged out silently.
        const remaining = this.ownItems(this.getQueue());
        if (remaining.length > 0) this.showRetryPanel(remaining);
        else this.removeRetryPanel();
      }
    } finally {
      try { localStorage.removeItem(this.SEND_LOCK_KEY); } catch {}
    }
  }

  // ══════════════════════════════════════════════════════════
  //  ONLINE / OFFLINE HANDLERS
  // ══════════════════════════════════════════════════════════

  private handleOffline(): void {
    this.cancelAutoSend();
    this.onlineStatus$.next(false);
    this.showBanner();
  }

  private handleOnline(): void {
    this.onlineStatus$.next(true);
    this.removeBanner();
    this.cleanStaleItems();   // reconnect re-check — surface any expiry before the retry panel

    // Nobody signed in (sign-in page): no panel of labels (patient names) — just say the network is back.
    if (this.currentStaffId() == null) { this.showToast('Network connection restored', 'success'); return; }

    const pending = this.ownItems(this.getQueue());
    if (pending.length > 0) {
      const going = pending.filter(q => whyAskFirst(q) === null).length;
      this.showToast(going > 0 ? `Network connection restored. Sending ${going} saved item(s)…` : 'Network connection restored', 'success');
      this.autoRetries = 0;
      this.scheduleAutoSend(AUTO_SEND_SETTLE_MS);
    } else {
      this.showToast('Network connection restored', 'success');
    }
  }

  // ══════════════════════════════════════════════════════════
  //  UI ELEMENTS
  // ══════════════════════════════════════════════════════════

  /** Red banner when offline */
  private showBanner(): void {
    if (this.bannerElement) return;

    const banner = document.createElement('div');
    banner.id = 'network-offline-banner';
    const NS = 'http://www.w3.org/2000/svg';   // must be const: the literal type picks the SVG overload of createElementNS
    const svg = document.createElementNS(NS, 'svg');
    for (const [k, v] of Object.entries({ width: '18', height: '18', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' })) svg.setAttribute(k, v);
    svg.style.flexShrink = '0';   // CSSOM write, not a style attribute (still allowed under a future style-src CSP)
    const shapes: [string, Record<string, string>][] = [
      ['line', { x1: '1', y1: '1', x2: '23', y2: '23' }],
      ['path', { d: 'M16.72 11.06A10.94 10.94 0 0 1 19 12.55' }],
      ['path', { d: 'M5 12.55a10.94 10.94 0 0 1 5.17-2.39' }],
      ['path', { d: 'M10.71 5.05A16 16 0 0 1 22.56 9' }],
      ['path', { d: 'M1.42 9a15.91 15.91 0 0 1 4.7-2.88' }],
      ['path', { d: 'M8.53 16.11a6 6 0 0 1 6.95 0' }],
      ['line', { x1: '12', y1: '20', x2: '12.01', y2: '20' }]
    ];
    for (const [tag, attrs] of shapes) {
      const s = document.createElementNS(NS, tag);
      for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v);
      svg.appendChild(s);
    }
    const text = NetworkGuardService.node('span');
    text.append(NetworkGuardService.node('strong', '', 'No Internet Connection'), " — What you save stays on this computer and sends by itself when the internet returns. Other staff can't see it until then: if urgent, tell them directly.");
    banner.append(svg, text);
    this.bannerElement = banner;
    Object.assign(banner.style, {
      position: 'fixed', top: '0', left: '0', right: '0',
      background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
      color: '#fff', padding: '10px 20px', fontSize: '13px',
      fontWeight: '500', fontFamily: "'Plus Jakarta Sans', sans-serif",
      zIndex: '100000', display: 'flex', alignItems: 'center',
      gap: '10px', justifyContent: 'center',
      boxShadow: '0 2px 12px rgba(220,38,38,0.4)'
    });

    // Inject animation styles once
    if (!document.getElementById('network-guard-styles')) {
      const style = document.createElement('style');
      style.id = 'network-guard-styles';
      style.textContent = `
        @keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `;
      document.head.appendChild(style);
    }

    banner.style.animation = 'slideDown 0.3s ease';
    document.body.prepend(banner);
  }

  private removeBanner(): void {
    this.bannerElement?.remove();
    this.bannerElement = null;
  }

  /** The items a person has to decide on (see whyAskFirst), or everything waiting when opened from the header. */
  private showRetryPanel(pending: PendingRequest[]): void {
    const showsAll = this.panelShowsAll;
    this.removeRetryPanel();
    this.panelShowsAll = showsAll;
    const mk = NetworkGuardService.node;

    const panel = mk('div');
    panel.id = 'network-retry-panel';
    panel.appendChild(mk('div', 'font-size:14px;font-weight:600;margin-bottom:8px;color:#059669;', `${pending.length} Unsaved Item(s)`));

    const list = mk('div', 'max-height:150px;overflow-y:auto;margin-bottom:12px;');
    for (const p of pending) {
      const row = mk('div', 'padding:6px 0;border-bottom:1px solid #e5e7eb;font-size:12px;overflow-wrap:anywhere;');
      row.append(mk('strong', '', NetworkGuardService.labelText(p.label)), ' ', mk('span', 'color:#6b7280;margin-left:8px;', this.ageText(this.ageMs(p))));
      const wallSaysOld = !p.refusedBecause && Date.now() - p.timestamp >= AUTO_SEND_MAX_AGE_MS && this.ageMs(p) < AUTO_SEND_MAX_AGE_MS;
      const why = wallSaysOld && whyAskFirst({ url: String(p.url), timestamp: Date.now() }) === null
        ? "This computer's clock changed after this was saved: check it before sending."
        : whyAskFirst({ url: String(p.url), timestamp: p.timestamp, refusedBecause: p.refusedBecause ? NetworkGuardService.labelText(p.refusedBecause) : undefined });
      if (why) row.append(mk('div', 'color:#B42318;margin-top:2px;', why));
      else row.append(mk('div', 'color:#6b7280;margin-top:2px;', 'Sends by itself as soon as the server answers.'));
      // TC-4467 (2026-11-03): the only way out of one refused item was "Discard all", which also threw away the good ones.
      if (p.id) {
        const removeBtn = mk('button', 'display:block;min-height:40px;padding:6px 12px;margin-top:4px;border-radius:6px;border:1px solid #d1d5db;background:#fff;color:#B42318;font-size:12px;font-weight:600;cursor:pointer;', 'Remove this item');
        removeBtn.type = 'button';
        removeBtn.className = 'retry-remove-btn';
        removeBtn.addEventListener('click', () => this.askRemoveOne(p, panel));
        row.appendChild(removeBtn);
      }
      list.appendChild(row);
    }
    panel.appendChild(list);

    const actions = mk('div', 'display:flex;gap:8px;justify-content:flex-end;');
    const discardBtn = mk('button', 'padding:6px 14px;border-radius:6px;border:1px solid #d1d5db;background:#fff;color:#374151;font-size:12px;font-weight:500;cursor:pointer;', 'Discard All');
    discardBtn.type = 'button';
    discardBtn.id = 'retry-discard-btn';
    const sendBtn = mk('button', 'padding:6px 14px;border-radius:6px;border:none;background:#059669;color:#fff;font-size:12px;font-weight:600;cursor:pointer;', 'Send All to Server');
    sendBtn.type = 'button';
    sendBtn.id = 'retry-send-btn';
    actions.append(discardBtn, sendBtn);
    panel.appendChild(actions);

    Object.assign(panel.style, {
      position: 'fixed', bottom: '24px', right: '24px',
      background: '#fff', borderRadius: '12px',
      // 360px phones: border-box keeps the whole panel on screen. 424px = the old 380px content + padding + border, so desktop is unchanged.
      boxSizing: 'border-box', padding: '16px 20px', width: '424px', maxWidth: 'calc(100vw - 48px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      border: '2px solid #059669',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      zIndex: '100002',
      animation: 'slideUp 0.3s ease'
    });

    this.retryPanelElement = panel;
    document.body.appendChild(panel);

    // Bound to the buttons themselves (no look-up by id), so nothing inside a label can take over a button.
    sendBtn.addEventListener('click', () => { this.panelShowsAll = false; this.retryAll(); });
    discardBtn.addEventListener('click', async () => {
      // On phones the pop-up opens as a bottom sheet over this corner: hide the panel while the question is open.
      panel.style.visibility = 'hidden';
      const ok = await this.dialog.confirm('Discard all unsaved data? This cannot be undone.', { tone: 'danger', okText: 'Discard all' });
      panel.style.visibility = '';
      if (ok) {
        this.panelShowsAll = false;
        this.clearQueue();
        this.showToast('Offline data discarded.', 'warning');
      }
    });
  }

  private async askRemoveOne(item: PendingRequest, panel: HTMLElement): Promise<void> {
    // On phones the pop-up opens as a bottom sheet over this corner: hide the panel while the question is open.
    panel.style.visibility = 'hidden';
    const ok = await this.dialog.confirm(
      `Remove "${NetworkGuardService.labelText(item.label)}"? It will not be sent, and this cannot be undone. Your other items stay.`,
      { tone: 'danger', okText: 'Remove' }
    );
    panel.style.visibility = '';
    if (!ok) return;
    this.removeFromQueue(item.id);
    this.refreshPending();
    const now = Date.now();
    const left = this.ownItems(this.getQueue());
    const show = this.panelShowsAll ? left : left.filter(q => whyAskFirst(q, now) !== null);
    if (show.length > 0) this.showRetryPanel(show);
    else this.removeRetryPanel();
    this.showToast('Item removed. It was not sent.', 'warning');
  }

  /** The server's reason when it understood the item and said no. Network trouble, an expired session (401), a blocked
   *  hospital (403) and "too many requests" are handled elsewhere and are worth retrying. */
  private static refusalReason(err: any): string | undefined {
    const s = err?.status;
    if (!(s >= 400 && s < 500) || s === 401 || s === 403 || s === 408 || s === 429) return undefined;
    return String(err?.error?.message || err?.error?.detail || err?.error?.title || 'The server refused this item.').slice(0, 300);
  }

  private removeRetryPanel(): void {
    this.panelShowsAll = false;
    this.retryPanelElement?.remove();
    this.retryPanelElement = null;
  }

  /**
   * PERSISTENT alert (no auto-dismiss) shown when offline items expired (72h) and were
   * removed. Staff must explicitly acknowledge — the last line of defence against SILENT
   * loss of clinical/financial data. Lists each dropped item so it can be re-entered.
   * Bottom-left. On screens narrower than about 900px it overlaps the retry panel and, being must-acknowledge, sits on top of it.
   */
  private showExpiredAlert(items: { label: string; timestamp: number }[]): void {
    this.removeExpiredAlert();
    const mk = NetworkGuardService.node;

    const alertEl = mk('div');   // not 'alert', which would shadow window.alert
    alertEl.id = 'network-expired-alert';
    alertEl.appendChild(mk('div', 'font-size:14px;font-weight:700;margin-bottom:6px;color:#b91c1c;', `⚠️ ${items.length} unsynced item(s) expired`));
    const note = mk('div', 'font-size:12px;color:#7f1d1d;margin-bottom:10px;line-height:1.5;', 'These were saved offline but never reached the server within 3 days and have now been removed. ');
    note.appendChild(mk('strong', '', 'Please re-enter them.'));
    alertEl.appendChild(note);

    const list = mk('div', 'max-height:160px;overflow-y:auto;margin-bottom:12px;');
    for (const p of items) {
      const row = mk('div', 'padding:6px 0;border-bottom:1px solid #fecaca;font-size:12px;color:#7f1d1d;overflow-wrap:anywhere;');
      row.append(mk('strong', '', NetworkGuardService.labelText(p.label)), ' ', mk('span', 'color:#b91c1c;margin-left:8px;', `queued ${this.timeAgo(p.timestamp)}`));
      list.appendChild(row);
    }
    alertEl.appendChild(list);

    const footer = mk('div', 'display:flex;justify-content:flex-end;');
    const ackBtn = mk('button', 'padding:6px 14px;border-radius:6px;border:none;background:#b91c1c;color:#fff;font-size:12px;font-weight:600;cursor:pointer;', "I've noted these");
    ackBtn.type = 'button';
    ackBtn.id = 'expired-ack-btn';
    footer.appendChild(ackBtn);
    alertEl.appendChild(footer);

    Object.assign(alertEl.style, {
      position: 'fixed', bottom: '24px', left: '24px',
      background: '#fff', borderRadius: '12px',
      boxSizing: 'border-box', padding: '16px 20px', width: '424px', maxWidth: 'calc(100vw - 48px)',
      boxShadow: '0 8px 32px rgba(185,28,28,0.25)',
      border: '2px solid #b91c1c',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      zIndex: '100003', animation: 'slideUp 0.3s ease'
    });
    this.expiredAlertElement = alertEl;
    document.body.appendChild(alertEl);
    ackBtn.addEventListener('click', () => {
      this.forgetExpiredForCurrentUser();
      this.removeExpiredAlert();
    });
  }

  private removeExpiredAlert(): void {
    this.expiredAlertElement?.remove();
    this.expiredAlertElement = null;
  }

  /** DISC-014: the network panels are built from DOM nodes. Data (labels, counts, times) only ever goes in as text, never as markup. */
  private static node<K extends keyof HTMLElementTagNameMap>(tag: K, css = '', text?: string): HTMLElementTagNameMap[K] {
    const el = document.createElement(tag);
    if (css) el.style.cssText = css;
    if (text != null) el.textContent = text;
    return el;
  }

  /** A queued or expired label as plain display text. Labels come from patient, staff and drug names and from localStorage, so they may hold anything. A string is shown as is, a number or boolean as its text, anything else as ''. Never throws. */
  private static labelText(v: unknown): string {
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    return '';
  }

  private timeAgo(timestamp: number): string {
    return this.ageText(Date.now() - timestamp);
  }

  private ageText(diffMs: number): string {
    if (diffMs < -60000) return 'time unclear';   // saved "in the future": the clock was wrong then, or is now
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  /** Toast notification */
  showToast(message: string, type: 'success' | 'warning' | 'error' = 'success', showForMs = 4000): void {
    const colors: Record<string, { bg: string; shadow: string }> = {
      success: { bg: 'linear-gradient(135deg, #059669, #047857)', shadow: 'rgba(5,150,105,0.3)' },
      warning: { bg: 'linear-gradient(135deg, #d97706, #b45309)', shadow: 'rgba(217,119,6,0.3)' },
      error:   { bg: 'linear-gradient(135deg, #dc2626, #b91c1c)', shadow: 'rgba(220,38,38,0.3)' }
    };
    const c = colors[type];

    const toast = document.createElement('div');
    toast.className = 'mt-net-toast';   // lets body.mt-locked (styles.scss) hide it behind the lock screen
    toast.textContent = message;
    if (message === SAVE_FAILED_TEXT) this.lastSaveFailedToast = toast;
    Object.assign(toast.style, {
      position: 'fixed', bottom: '24px', right: '24px',
      background: c.bg, color: '#fff', padding: '12px 20px',
      borderRadius: '10px', fontSize: '13px', fontWeight: '500',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      zIndex: '100001', maxWidth: '400px',
      boxShadow: `0 4px 16px ${c.shadow}`,
      transition: 'opacity 0.3s ease, transform 0.3s ease',
      transform: 'translateY(10px)', opacity: '0'
    });

    document.body.appendChild(toast);
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
    });

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(10px)';
      setTimeout(() => toast.remove(), 300);
    }, showForMs);
  }
}
